import React, { useState } from 'react';
import { TextField, Autocomplete, Typography, Box, Grid, Button, Chip } from '@mui/material';
import BaseModal from './BaseModal';

const NominationModal = ({
  open,
  onClose,
  onSubmit,
  loading,
  reportType,
  availableRegions,
  availableBranches,
  trainingTypes,
  dispatch,
}) => {
  // Create "All trainings" option
  const allTrainingsOption = {
    id: 'ALL',
    trainingName: 'All Trainings',
    isAll: true,
  };

  // Combine "All trainings" option with training types
  const trainingTypeOptions = [allTrainingsOption, ...(trainingTypes || [])];

  const [filters, setFilters] = useState({
    trainingType: null,
    dateRange: {
      startDate: null,
      endDate: null,
    },
    regions: [],
  });

  const handleTrainingTypeChange = (event, value) => {
    setFilters((prev) => ({ ...prev, trainingType: value }));
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const handleRegionChange = (event, value) => {
    setFilters((prev) => ({ ...prev, regions: value }));
  };

  const handleSelectAllRegions = () => {
    const allRegions = availableRegions || [];
    setFilters((prev) => ({ ...prev, regions: allRegions }));
  };

  const handleClearAllRegions = () => {
    setFilters((prev) => ({ ...prev, regions: [] }));
  };

  const handleSubmit = () => {
    const payload = {
      trainingTypeId: filters.trainingType?.isAll ? null : filters.trainingType?.id,
      trainingTypeName: filters.trainingType?.isAll
        ? 'All Trainings'
        : filters.trainingType?.trainingName,
      isAllTrainings: filters.trainingType?.isAll || false,
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate,
      regionIds: filters.regions.map((r) => r.id),
      regionNames: filters.regions.map((r) => r.label),
    };

    onSubmit(payload);
  };

  const handleClose = () => {
    setFilters({
      trainingType: null,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      regions: [],
    });
    onClose();
  };

  const isSubmitDisabled =
    !filters.trainingType ||
    !filters.dateRange.startDate ||
    !filters.dateRange.endDate ||
    filters.regions.length === 0;

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      loading={loading}
      reportType={reportType}
      submitDisabled={isSubmitDisabled}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
          Select Filters
        </Typography>

        <Grid container spacing={3}>
          {/* Training Type */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Training Type *
            </Typography>
            <Autocomplete
              options={trainingTypeOptions}
              getOptionLabel={(option) => option.trainingName || ''}
              value={filters.trainingType}
              onChange={handleTrainingTypeChange}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id || option.trainingName}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: option.isAll ? 600 : 400,
                      color: option.isAll ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {option.trainingName}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select training type"
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Start Date *
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={filters.dateRange.startDate || ''}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              End Date *
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={filters.dateRange.endDate || ''}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
              }}
            />
          </Grid>

          {/* Regions */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Regions *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleSelectAllRegions}
                  sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClearAllRegions}
                  sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <Autocomplete
              multiple
              options={availableRegions || []}
              getOptionLabel={(option) => option.label || ''}
              value={filters.regions}
              onChange={handleRegionChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingRight: '65px !important',
                  maxHeight: '100px !important',
                  overflow: 'scroll !important',
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select regions"
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.label}
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))
              }
            />
          </Grid>
        </Grid>
      </Box>
    </BaseModal>
  );
};

export default NominationModal;
